/* eslint-disable @typescript-eslint/indent */
import React, { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { Form, FormSpy, Field } from 'react-final-form';
import { FormState, Tools, MutableState, InternalFormState } from 'final-form';
import { TafButton } from '@taf-ui/react';

import { isBrand, isProductWithMandatorySSN, translate } from 'utils/utils';
import {
  isEmail,
  isLocalZipCode,
  isRequired,
  isValidPhone,
  isValidSSN,
  isNumberNumeric,
  validateDate,
} from 'utils/validations';
import AppStorage from 'Storage/Storage';
import PersonalDataForm from 'interfaces/PersonalDataForm';
import DraftService from 'Requests/DraftService/DraftService';
import RouteParams from 'interfaces/RouteParams';
import { AddressEnum } from '../interfaces/Address';
import { Loader } from 'Loading/Loading';
import Steps from 'enums/Steps';
import Labels from 'enums/Labels';
import Brand from '../enums/Brand';
import InputName from 'FormInputs/InputName/InputName';
import InputGender from 'FormInputs/InputGender/InputGender';
import InputDateOfBirth from 'FormInputs/InputDateOfBirth/InputDateOfBirth';
import InputNationality from 'FormInputs/InputNationality/InputNationality';
import InputSSN from 'FormInputs/InputSSN/InputSSN';

import InputPhoneNumber from 'FormInputs/InputPhoneNumber/InputPhoneNumber';
import InputEmailAddress from 'FormInputs/InputEmailAddress/InputEmailAddress';
import InputText from 'FormInputs/InputText/InputText';
import InputAddress, {
  UpdatedAddress,
  UpdatedCountry,
} from 'FormInputs/InputAddress/InputAddress';
import InputGP from 'FormInputs/InputGP/InputGP';
import Requests from 'Requests/Requests';
import { Navigate } from 'react-router-dom';
import { validUpdateInsuredInfoFields } from './utils';
import UnderwriterOwner from '../enums/UnderwriterOwner';
import { MagnumStartCaseRequestParameters } from '../interfaces/Magnum';

import ReadSpeakerReader from '../ReadSpeakerReader';
import { languageToReadSpeakerLang } from '../ReadSpeakerReader/ReadSpeakerProvider';

import './personalDetails.css';

interface PersonalDetailsState {
  navigateComponent?: ReactNode;
}

class PersonalDetails extends React.PureComponent<
  RouteParams,
  PersonalDetailsState
> {
  private readonly storage: AppStorage;

  private readonly draftService: DraftService;

  private submitButtonRef: HTMLButtonElement | null = null;

  private readonly locationSearch: string | undefined;

  private readonly isWefox: boolean;

  constructor(props: RouteParams) {
    super(props);

    this.locationSearch = props.locationSearch as string;
    this.storage = AppStorage.getInstance();
    this.draftService = new DraftService();

    this.isWefox = isBrand(Brand.wefox);

    this.state = {
      navigateComponent: undefined,
    };
  }

  componentDidMount(): void {
    this.storage.modifyProgressBar(Steps.All, 'active', false);
    this.storage.modifyProgressBar(Steps.PersonalData, 'active', true);
    this.draftService.syncLater();
    this.checkStepError();
  }

  componentWillUnmount(): void {
    this.storage.modifyProgressBar(Steps.PersonalData, 'active', false);
    this.storage.modifyProgressBar(Steps.PersonalData, 'done', true);
    this.storage.modifyProgressBar(
      Steps.PersonalData,
      'valid',
      this.storage.getIsPersonalDataFormValid,
    );
  }

  checkStepError = (): void => {
    if (
      !this.storage.getIsPersonalDataFormValid &&
      this.storage.getActiveStep.done
    ) {
      setTimeout(() => {
        if (this.submitButtonRef) {
          this.submitButtonRef.click();
        }
      }, 0);
    }
  };

  private onSubmit = (): void => {};

  // eslint-disable-next-line @typescript-eslint/ban-types
  validate = (
    values: Partial<PersonalDataForm>,
  ): Partial<PersonalDataForm | {}> => {
    const errors = {};

    if (this.isWefox) {
      isRequired('firstName', errors, values);
    } else {
      isRequired('initials', errors, values);
    }

    isRequired('surname', errors, values);
    validateDate('birthDate', errors, values);
    isRequired('birthDate', errors, values);
    isRequired('nationality', errors, values);

    if (!this.isWefox) {
      /* TAF local address */
      isRequired('zipCode', errors, values);
      isLocalZipCode('zipCode', errors, values);
    } else {
      /* wefox international address */
      isRequired('zipCodeInt', errors, values);
      isRequired('country', errors, values);
    }

    /* common part for both */
    isRequired('street', errors, values);
    isRequired('city', errors, values);
    isNumberNumeric('number', errors, values);
    isRequired('number', errors, values);

    isRequired('gpCity', errors, values);

    isRequired('greeting', errors, values);
    isRequired('email', errors, values);
    isEmail('email', errors, values);
    isValidPhone('phonePrefix', 'phoneNumberBody', errors, values);

    isRequired('gpName', errors, values);

    const isSSNMandatory =
      isProductWithMandatorySSN(this.storage.getProductType()) &&
      this.storage.isDeductPremiumFromTaxes();

    if (isSSNMandatory && !this.isWefox) {
      isRequired('socialSecurityNumber', errors, values);
    }

    if (values.socialSecurityNumber && !this.isWefox) {
      isValidSSN('socialSecurityNumber', errors, values);
    }

    return errors;
  };

  getInitialValues = (): Partial<PersonalDataForm> => ({
    ...this.storage.getPersonalDataFormState,
  });

  saveFormState = (
    state: FormState<Partial<PersonalDataForm>> | InternalFormState,
  ): void => {
    this.storage.setIsPersonalDataFormValid(state.valid);
    this.storage.setPersonalDataFormState({
      ...state.values,
      addressType: this.isWefox ? AddressEnum.International : AddressEnum.Dutch,
    });
    this.draftService.saveDraft();
  };

  goToNextStep = (): void => {
    const { healthDeclarationId } = this.props;
    this.setState({
      navigateComponent: (
        <Navigate
          to={`/${healthDeclarationId}/questionnaire${this.locationSearch || ''}`}
          replace={true}
        />
      ),
    });
  };

  insuredFieldsShouldUpdate = (): void => {
    const {
      email,
      gender,
      initials,
      firstName,
      middleName,
      surname,
      birthDate,
    } = this.storage.getPersonalDataFormState;

    const validatedFields = validUpdateInsuredInfoFields({
      email,
      gender,
      initials,
      middleName,
      firstName,
      surname,
      dateOfBirth: birthDate,
    });

    if (validatedFields) {
      Requests.updateInsuredInfo(validatedFields)
        .then(() => {
          console.log('Insured updated'); // eslint-disable-line no-console
        })
        .catch((err: string | undefined) => {
          throw new Error(err);
        });
      if (this.storage.getUnderwriterOwner === UnderwriterOwner.IPT_MAGNUM) {
        const data: MagnumStartCaseRequestParameters = {
          dateOfBirth: validatedFields.dateOfBirth,
          gender: validatedFields.gender,
          initials: validatedFields.initials || '',
          language: this.storage.getLocale,
          middleName: validatedFields?.middleName,
          surname: validatedFields.surname,
        };
        // eslint-disable-line
        Requests.magnum
          .reinitialize(data)
          .then(() => {
            console.log('Insured updated for magnum case'); // eslint-disable-line no-console
          })
          .catch((err: string | undefined) => {
            throw new Error(err);
          });
      }
    }
  };

  render(): React.ReactElement {
    const { insuredFieldsShouldUpdate } = this;
    const initialValues = this.getInitialValues();

    if (!initialValues.gender) {
      return <Loader />;
    }

    const lang = this.storage.getLocale;

    const { navigateComponent } = this.state;

    if (navigateComponent) {
      return <>{navigateComponent}</>;
    }

    return (
      <Form
        keepDirtyOnReinitialize={true}
        initialValuesEqual={(): boolean => true}
        onSubmit={this.onSubmit}
        initialValues={initialValues}
        validate={this.validate}
        mutators={{
          updateAddress: (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            args: UpdatedAddress[],
            state: MutableState<Partial<PersonalDataForm>>,
            { changeValue }: Tools<Partial<PersonalDataForm>>,
          ): void => {
            changeValue(state, 'street', () => args[0].street);
            changeValue(state, 'city', () => args[0].city);
            this.saveFormState(state.formState);
          },
          updateCountry: (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            args: UpdatedCountry[],
            state: MutableState<Partial<PersonalDataForm>>,
            { changeValue }: Tools<Partial<PersonalDataForm>>,
          ): void => {
            changeValue(state, 'country', () => args[0].country);
          },
        }}
        render={({ handleSubmit, form }): React.ReactElement => (
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          <form onSubmit={handleSubmit}>
            <div
              className="personal-details"
              id="main-content"
              lang={languageToReadSpeakerLang(lang)}
            >
              <div className="personal-details-header">
                <ReadSpeakerReader />
                <h1>{translate(Labels.PERSONAL_DETAILS_HEADER1, lang)}</h1>

                <p>{translate(Labels.PERSONAL_DETAILS_P1, lang)}</p>
              </div>

              <>
                <div className="form-left space-between">
                  <FormSpy
                    onChange={(
                      formState: FormState<Partial<PersonalDataForm>>,
                    ): void => {
                      this.saveFormState(formState);
                    }}
                  />

                  <h2 className="personal-details-subheader">
                    {translate(Labels.PERSONAL_DETAILS_HEADER2, lang)}
                  </h2>

                  <InputName onBlurHandler={insuredFieldsShouldUpdate} />

                  <div className="row">
                    <InputGender onBlurHandler={insuredFieldsShouldUpdate} />
                    <InputDateOfBirth
                      onBlurHandler={insuredFieldsShouldUpdate}
                    />
                  </div>

                  <div className="row">
                    <InputNationality
                      fieldName="nationality"
                      placeholder={Labels.PERSONAL_DETAILS_NATIONALITY}
                      label={Labels.PERSONAL_DETAILS_NATIONALITY}
                    />

                    {!this.isWefox && <InputSSN />}
                  </div>

                  <div className="row">
                    <InputPhoneNumber />
                    <InputEmailAddress
                      onBlurHandler={insuredFieldsShouldUpdate}
                      lang={lang}
                      required={true}
                    />
                  </div>

                  <InputAddress
                    name="insured"
                    initialInternationalAddressState={this.isWefox}
                    addressModel={{
                      zipCode: 'zipCode',
                      zipCodeInt: 'zipCodeInt',
                      houseNumber: 'number',
                      houseLetter: 'suffix',
                      street: 'street',
                      city: 'city',
                    }}
                    mutator={form.mutators.updateAddress}
                    mutatorForCountry={form.mutators.updateCountry}
                  />
                </div>
                <div className="personal-details-form form-right space-between">
                  <h2 className="personal-details-subheader">
                    {translate(Labels.PERSONAL_DETAILS_HEADER3, lang)}
                  </h2>

                  <InputGP />

                  <Field name="gpCity">
                    {({ input, meta }): React.ReactElement => (
                      <InputText
                        label={Labels.PERSONAL_DETAILS_RESIDENCE}
                        input={input}
                        meta={meta}
                        isRequired={true}
                      />
                    )}
                  </Field>

                  <p>{translate(Labels.PERSONAL_DETAILS_P2, lang)}</p>

                  <button
                    id="personalDetailsPageSubmitBtn"
                    type="submit"
                    className="primary submit-personal-form-button rs_skip"
                    ref={(ref): void => {
                      this.submitButtonRef = ref;
                    }}
                  >
                    Submit
                  </button>

                  <TafButton
                    id="personalDetailsPageNextBtn"
                    onClick={this.goToNextStep}
                    type="button"
                    primary={true}
                    className="proceed"
                    title={translate(Labels.NEXT_STEP, lang)}
                  >
                    {translate(Labels.NEXT_STEP, lang)}
                  </TafButton>
                </div>
              </>
            </div>
          </form>
        )}
      />
    );
  }
}

export default observer(PersonalDetails);
